import awardsService from '../../api/awards-service';
import { removeArrayItem, updateArrayItem } from '../../util/array';
import { mapErrorsToInputs, scrollToFirstError } from '../../util/forms';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '../../util/event-bus';
import i18n from '../../i18n/i18n-config';

const state = {
  awards: null,
  awardsPagination: {},
  editedAward: {},
  awardsValidationErrors: {},
  awardsFilterParams: {},
  loadingAwards: false,
  savingAward: false,
};

const mutations = {
  SET_AWARDS(state, { data, current_page, per_page, total }) {
    state.awards = data;
    state.awardsPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.awardsFilterParams = params;
  },

  STORE_AWARD(state, award) {
    state.awards = [...state.awards, award];
    state.awardsValidationErrors = {};
    state.awardsPagination.total += 1;
  },

  SET_EDITED_AWARD(state, award) {
    state.awardsValidationErrors = {};
    state.editedAward = JSON.parse(JSON.stringify(award));
  },

  UPDATE_AWARDS(state, award) {
    state.awards = updateArrayItem(state.awards, award);
  },

  DELETE_AWARD(state, award) {
    state.awards = removeArrayItem(state.awards, award);
    state.awardsPagination.total -= 1;
  },

  SET_AWARDS_VALIDATION_ERRORS(state, validationErrors) {
    state.awardsValidationErrors = validationErrors;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.awardsValidationErrors[field];
  },

  SET_LOADING_AWARDS(state, loading) {
    state.loadingAwards = loading;
  },

  SET_SAVING_AWARD(state, saving) {
    state.savingAward = saving;
  },
};

const actions = {
  fetchAwards({ commit }, params) {
    commit('SET_LOADING_AWARDS', true);
    return awardsService
      .getPage(params)
      .then((res) => {
        commit('SET_AWARDS', res.data);
        if (params.page !== state.awardsFilterParams.page) {
          window.scrollTo(0, 0);
        }
        commit('SET_FILTER_PARAMS', params);
      })
      .finally(() => {
        commit('SET_LOADING_AWARDS', false);
      });
  },

  async storeAwards({ commit }) {
    commit('SET_SAVING_AWARD', true);
    const res = await awardsService.create();
    commit('SET_SAVING_AWARD', false);
    commit('STORE_AWARD', res.data);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('award_created'));
    return res;
  },

  editAward({ state, commit }, awardId) {
    const award = state.awards?.find((a) => a.id === awardId);
    if (award) {
      commit('SET_EDITED_AWARD', award);
      return Promise.resolve(award);
    }
    return awardsService.getById(awardId).then((res) => {
      commit('SET_EDITED_AWARD', res.data);
    });
  },

  updateAward({ commit }, award) {
    commit('SET_SAVING_AWARD', true);
    return awardsService
      .update(award)
      .then((res) => {
        commit('UPDATE_AWARDS', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('award_updated'));
      })
      .catch((err) => {
        commit('SET_AWARDS_VALIDATION_ERRORS', mapErrorsToInputs(err));
        scrollToFirstError();
        throw err;
      })
      .finally(() => {
        commit('SET_SAVING_AWARD', false);
      });
  },

  deleteAward({ commit }, award) {
    openConfirmDialog({
      title: i18n.t('confirm_award_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      awardsService.delete(award).then(() => {
        commit('DELETE_AWARD', award);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('award_deleted'));
      });
    });
  },

  clearAwardsValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
